import React, {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import router, {editLoyaltyReward, newLoyaltyRewardPart, Route} from "@/routing/Router";
import AdminLayout from "@widgets/layout/AdminLayout";
import {useQuery} from "@tanstack/react-query";
import {RestaurantLocation} from "@/data/entities/restaurant";
import {SetStateFn} from "@/utils/SetStateUtils";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import LoyaltyRewardEditor from "@widgets/loyaltyRewards/editor/LoyaltyRewardEditor";
import {api} from "@/data/api";
import {LoyaltyRewardDetails} from "@/data/entities/loyaltyReward";

const getOptionalLoyaltyReward = async (
    featuredProductId: string | null | undefined,
    dashboardLocation: RestaurantLocation,
): Promise<LoyaltyRewardDetails | null> => {
    if (featuredProductId === undefined || featuredProductId === null || featuredProductId === newLoyaltyRewardPart) {
        return null;
    }

    return api.loyaltyReward.getLoyaltyRewardDetails(dashboardLocation.id, parseInt(featuredProductId));
};

export type SetLoyaltyRewardProps = {
    loyaltyReward: LoyaltyRewardDetails | null,
    setLoyaltyReward: SetStateFn<LoyaltyRewardDetails | null>,
};

const LoyaltyReward = () => {
    const {loyaltyRewardId} = useParams();
    const [loyaltyReward, setLoyaltyReward] = useState<LoyaltyRewardDetails | null>(null);
    const {dashboardLocation} = useRestaurant();
    const {data, isLoading, error} = useQuery({
        queryKey: ["loyalty-reward", loyaltyRewardId || "new", dashboardLocation],
        queryFn: () => getOptionalLoyaltyReward(loyaltyRewardId, dashboardLocation),
    });
    const route: Route = useMemo(
        () => loyaltyReward ? editLoyaltyReward(loyaltyReward.id ?? -1) : router.newLoyaltyReward,
        [loyaltyReward]);

    // Place fetched entity in state
    useEffect(() => {
        if (data !== undefined && data !== null) {
            setLoyaltyReward(data);
        }
    }, [data]);

    // Trigger error page to show
    if (error) {
        throw new Error("Cannot load loyalty reward");
    }

    return (
        <AdminLayout currentPage={route}>
            <LoyaltyRewardEditor
                key={loyaltyReward?.id || "new"}
                route={route}
                loyaltyReward={loyaltyReward}
                setLoyaltyReward={setLoyaltyReward}
                initialLoading={isLoading}
                restaurantId={dashboardLocation.id}
            />
        </AdminLayout>
    );
};

export default LoyaltyReward;